import { create } from "zustand";
import type { AccFileSelection } from "./accBrowseStore";

export type AccModelStatus = "idle" | "loading" | "ready" | "error";

export interface ApsViewable {
  /** Bubble node guid — stable sheet id for the picker. */
  id: string;
  name: string;
  role: string;
  kind: "2d" | "3d";
}

interface AccModelState {
  status: AccModelStatus;
  progressMessage: string;
  viewerUrn: string | null;
  viewables: ApsViewable[];
  /** All APS viewables (unfiltered) — used to pick the best 3D for extraction. */
  allViewables: ApsViewable[];
  currentViewableId: string | null;
  error: string | null;
  selectionKey: string | null;
  selection: AccFileSelection | null;
  setProgress: (message: string) => void;
  setLoading: (selection: AccFileSelection) => void;
  setReady: (args: {
    viewerUrn: string;
    viewables: ApsViewable[];
    defaultViewableId: string;
    selection: AccFileSelection;
  }) => void;
  setViewable: (id: string) => void;
  setError: (message: string) => void;
  clear: () => void;
}

function selectionKey(sel: AccFileSelection): string {
  return `${sel.hubId}:${sel.projectId}:${sel.itemId}`;
}

function pickDefaultViewable(viewables: ApsViewable[]): string {
  const floor2d = viewables.find(
    v => v.kind === "2d" && /a2|floor\s*plan|floor plan/i.test(v.name)
  );
  if (floor2d) return floor2d.id;
  const any2d = viewables.find(v => v.kind === "2d");
  if (any2d) return any2d.id;
  const any3d = viewables.find(v => v.kind === "3d");
  if (any3d) return any3d.id;
  return viewables[0]?.id ?? "";
}

export const useAccModelStore = create<AccModelState>((set, get) => ({
  status: "idle",
  progressMessage: "",
  viewerUrn: null,
  viewables: [],
  allViewables: [],
  currentViewableId: null,
  error: null,
  selectionKey: null,
  selection: null,

  setProgress: message => set({ progressMessage: message }),

  setLoading: selection =>
    set({
      status: "loading",
      progressMessage: "Preparing ACC model…",
      viewerUrn: null,
      viewables: [],
      allViewables: [],
      currentViewableId: null,
      error: null,
      selectionKey: selectionKey(selection),
      selection,
    }),

  setReady: ({ viewerUrn, viewables, defaultViewableId, selection }) =>
    set({
      status: "ready",
      progressMessage: "",
      viewerUrn,
      viewables,
      allViewables: viewables,
      currentViewableId: defaultViewableId || pickDefaultViewable(viewables),
      error: null,
      selectionKey: selectionKey(selection),
      selection,
    }),

  setViewable: id => {
    if (get().currentViewableId === id) return;
    set({ currentViewableId: id });
  },

  setError: message =>
    set({
      status: "error",
      progressMessage: "",
      error: message,
      viewerUrn: null,
      viewables: [],
      allViewables: [],
      currentViewableId: null,
    }),

  clear: () =>
    set({
      status: "idle",
      progressMessage: "",
      viewerUrn: null,
      viewables: [],
      allViewables: [],
      currentViewableId: null,
      error: null,
      selectionKey: null,
      selection: null,
    }),
}));

export { pickDefaultViewable };
